import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, timer } from 'rxjs';
import { map, retry } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { HeaderHelperService } from '../header-helper.service';
import { BaseApiResponse } from '../../models/api/api.model';
import { SUPPRESS_ERROR_TOAST } from '../../error.interceptor';

const MAX_AI_ATTEMPTS = 3;

function isRetryableStatus(status: number): boolean {
  return status === 424 || status === 429 || status >= 500;
}

export interface AiChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface PatientContext {
  patientName?: string;
  patientAge?: string;
  woundType?: string;
  woundLocation?: string;
  woundSize?: string;
  woundStage?: string;
  lastAssessmentDate?: string;
  treatmentPlan?: string;
  woundHistory?: WoundHistoryEntry[];
}

export interface WoundHistoryEntry {
  date: string;
  woundSize: string;
  woundStage: string;
  notes: string;
}

export interface PatientSummaryRequest {
  patientName?: string;
  age?: number;
  gender?: string;
  height?: number;
  weight?: number;
  maritalStatus?: string;
  bloodType?: string;
  skinType?: string;
  dietType?: string;
  smokingStatus?: string;
  alcoholConsumption?: string;
  livingConditions?: string;
  placeOfResidence?: string;
  bmi?: number;
  abiValue?: string;
  medications?: string[];
  antibiotics?: string[];
  allergies?: string[];
  surgeries?: string[];
  debridements?: string[];
  woundCareProducts?: string[];
  wounds?: PatientSummaryWoundDto[];
  language: string;
}

export interface PatientSummaryWoundDto {
  type?: string;
  location?: string;
  status?: string;
  stage?: string;
  size?: string;
  lastAssessmentDate?: string;
}

@Injectable({ providedIn: 'root' })
export class AiAssistantService {
  private readonly baseUrl = `${environment.apiLocations.communication}/ai-chat`;

  constructor(
    private headerHelperService: HeaderHelperService,
    private http: HttpClient
  ) {}

  generatePatientSummary(request: PatientSummaryRequest): Observable<{ summary: string }> {
    return this.http.post<BaseApiResponse<{ summary: string }>>(
      `${this.baseUrl}/patient-summary`,
      request,
      {
        headers: this.headerHelperService.getHeaders(),
        context: new HttpContext().set(SUPPRESS_ERROR_TOAST, true),
      }
    ).pipe(
      map(res => res.data),
      retry({
        count: MAX_AI_ATTEMPTS - 1,
        delay: (error, attempt) => {
          if (!isRetryableStatus(error?.status)) throw error;
          const retryAfterSec = error?.headers?.get?.('Retry-After');
          const delayMs = (error?.status === 429 && retryAfterSec)
            ? parseInt(retryAfterSec, 10) * 1000
            : attempt * 1500;
          return timer(delayMs);
        },
      })
    );
  }

  streamChat(
    messages: AiChatMessage[],
    patientContext: PatientContext | null,
    imageUrls: string[],
    onChunk: (text: string) => void,
    onDone: () => void,
    onError: (err: any) => void,
    signal?: AbortSignal
  ): void {
    const headers = this.headerHelperService.getHeaders();
    const headersObj: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };
    headers.keys().forEach((key) => {
      headersObj[key] = headers.get(key)!;
    });

    this.fetchWithRetry(
      `${this.baseUrl}/stream`,
      {
        method: 'POST',
        headers: headersObj,
        body: JSON.stringify({ messages, patientContext, imageUrls }),
        signal,
      }
    )
      .then(async (response) => {
        if (!response.body) {
          console.error('[AI Assistant] Response body is empty');
          throw new Error('Response body is empty');
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let currentEvent = 'message';

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            const trimmed = line.trim();
            if (trimmed.startsWith('event:')) {
              currentEvent = trimmed.slice(6).trim();
              continue;
            }
            if (!trimmed.startsWith('data:')) continue;
            const data = trimmed.slice(5).trim();
            if (data === '[DONE]') {
              onDone();
              return;
            }
            if (currentEvent === 'error') {
              console.error('[AI Assistant] SSE event:error received, data:', data);
              try {
                const parsed = JSON.parse(data);
                onError(new Error(parsed?.error ?? data));
              } catch {
                onError(new Error(data));
              }
              currentEvent = 'message';
              return;
            }
            currentEvent = 'message';
            try {
              const text = JSON.parse(data);
              if (typeof text === 'string' && text.length > 0) onChunk(text);
            } catch {
              // ignore non-JSON keep-alive/sse metadata lines
            }
          }
        }
        if (buffer.trim().startsWith('data:')) {
          const data = buffer.trim().slice(5).trim();
          if (data === '[DONE]') {
            onDone();
            return;
          }
          if (currentEvent === 'error') {
            console.error('[AI Assistant] SSE event:error in trailing buffer, data:', data);
            try {
              const parsed = JSON.parse(data);
              onError(new Error(parsed?.error ?? data));
            } catch {
              onError(new Error(data));
            }
            return;
          }
          if (data) {
            try {
              const text = JSON.parse(data);
              if (typeof text === 'string' && text.length > 0) onChunk(text);
            } catch {
              // ignore incomplete/non-JSON trailing payload
            }
          }
        }
        onDone();
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') {
          console.error('[AI Assistant] Fetch/stream exception caught:', err);
          onError(err);
        }
      });
  }

  private async fetchWithRetry(url: string, options: RequestInit): Promise<Response> {
    for (let attempt = 1; attempt <= MAX_AI_ATTEMPTS; attempt++) {
      const response = await fetch(url, options);
      if (response.ok) return response;

      if (!isRetryableStatus(response.status) || attempt === MAX_AI_ATTEMPTS) {
        console.error('[AI Assistant] HTTP error:', response.status, response.statusText);
        throw new Error(`HTTP ${response.status}`);
      }

      const retryAfter = response.headers.get('Retry-After');
      const delayMs = (response.status === 429 && retryAfter)
        ? parseInt(retryAfter, 10) * 1000
        : attempt * 1500;

      console.warn(`[AI Assistant] Retryable error ${response.status}, attempt ${attempt}/${MAX_AI_ATTEMPTS}, retrying in ${delayMs}ms`);
      await new Promise<void>(resolve => setTimeout(resolve, delayMs));
    }
    throw new Error('Max retries exceeded');
  }
}
